import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box } from '@material-ui/core';

import AppContext from '../../../AppContext';
import Button from '../../../components/Button';
import DataTable, {
  cancelOnRowClick,
  Filter,
} from '../../../components/DataTable';
import Page from '../../../components/Page';
import {
  COLUMN_EXAM_ADDRESS,
  COLUMN_EXAM_DATE,
  COLUMN_EXAM_DIPLOMAS,
  COLUMN_EXAM_LOCATION,
} from '../../exams/domain/columns';
import { Exam, User } from '../../../types';
import { EXAM_BASE_ROUTE } from '../../exams';
import CandidateExamRegisterDialog from '../components/CandidateExamRegisterDialog';
import CandidateExamsRepository from '../repository/CandidateExamsRepository';
import { DiplomaCategory } from '../../diploma/domain/types';
import DelegateRepository from '../repository/DelegateRepository';
import Loader from '../../../components/Loader';

const SelectedExamOverview = () => {
  const history = useHistory();
  const { roleViewManager } = useContext(AppContext);
  const account = useSelector(
    (selector: {
      user: {
        account: User;
      };
    }) => selector.user.account,
  );

  const [diplomaCategories, setDiplomaCategories] = useState<
    DiplomaCategory[] | undefined
  >(undefined);

  const delegateId = roleViewManager.getUser().id || '';
  const repository = new CandidateExamsRepository(delegateId);

  const [registrationDialogProps, setRegistrationDialogProps] = useState<{
    exam: Exam | null;
    open: boolean;
  }>({ exam: null, open: false });

  const handleRegistrationDialogOpen = (exam: Exam) =>
    setRegistrationDialogProps({ exam, open: true });
  const handleRegistrationDialogClose = () =>
    setRegistrationDialogProps({ exam: null, open: false });

  const columns = [
    COLUMN_EXAM_DATE,
    COLUMN_EXAM_LOCATION,
    COLUMN_EXAM_DIPLOMAS,
    COLUMN_EXAM_ADDRESS,
    {
      name: '',
      field: 'register',
      render: (exam: Exam) => (
        <Box onClick={cancelOnRowClick}>
          <Button
            icon={['fal', 'plus']}
            label="Aanmelden"
            iconPosition="right"
            onClick={() => handleRegistrationDialogOpen(exam)}
          />
        </Box>
      ),
    },
  ];

  const filters: Filter[] = [
    {
      field: 'date',
      name: 'Datum',
      type: 'dateRange',
    },
    {
      field: 'examLocation',
      name: 'Locatie',
      type: 'text',
    },
  ];

  if (diplomaCategories !== undefined && diplomaCategories.length >= 1) {
    filters.push({
      field: 'diplomaCategories',
      name: 'Diplomacategoriëen',
      type: 'checkbox',
      options: diplomaCategories.map((category) => ({
        value: category.id,
        label: category.name,
      })),
    });
  }

  const onRowClick = (exam: Exam) => {
    history.push(`${EXAM_BASE_ROUTE}/${exam.id}`);
  };

  const styleRow = (item: Exam) =>
    item?.highPriority ? ('trHighlight' as const) : ('tr' as const);

  useEffect(() => {
    new DelegateRepository()
      .getDiplomaCategories(account.id)
      .then((res) => setDiplomaCategories(res.data));
  }, []);

  // make sure diploma categories are loaded, because they are needed in the filters of the table
  if (diplomaCategories === undefined) {
    return <Loader />;
  }

  return (
    <Page title="Geselecteerd voor jou">
      {registrationDialogProps.exam && (
        <CandidateExamRegisterDialog
          exam={registrationDialogProps.exam}
          open={registrationDialogProps.open}
          onClose={handleRegistrationDialogClose}
        />
      )}
      <DataTable
        id="delegate-selected-exam-overview"
        repository={repository}
        columns={columns}
        filters={filters}
        persistFilters
        onRowClick={onRowClick}
        styleRow={styleRow}
        triggerSearchManually
      />
    </Page>
  );
};

export default SelectedExamOverview;
